import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import SystemNoticeRichEditor from '../components/SystemNoticeRichEditor'
import { api, resolveApiAssetUrl } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { formatMenuInstant } from '../lib/date'
import SanitizedRichText from '../ui/SanitizedRichText'

function unwrapData(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res?.data
}

const STATUS_OPTIONS = [
  { id: 'ALL', label: '전체' },
  { id: 'ONGOING', label: '진행중' },
  { id: 'UPCOMING', label: '진행예정' },
  { id: 'ENDED', label: '종료' },
]

const STATUS_LABELS = {
  UPCOMING: '진행예정',
  ONGOING: '진행중',
  ENDED: '종료',
}

const STATUS_BADGE_CLASS = {
  UPCOMING: 'bg-sky-50 border-sky-200 text-sky-700',
  ONGOING: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  ENDED: 'bg-slate-50 border-slate-200 text-slate-600',
}

function eventStatusLabel(status) {
  return STATUS_LABELS[status] || '진행중'
}

function eventStatusBadgeClass(status) {
  return STATUS_BADGE_CLASS[status] || STATUS_BADGE_CLASS.ONGOING
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

function toDatetimeLocalValue(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

function defaultStartValue() {
  return toDatetimeLocalValue(new Date())
}

function defaultEndValue() {
  const d = new Date()
  d.setDate(d.getDate() + 7)
  return toDatetimeLocalValue(d)
}

function isoFromDatetimeLocal(value) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

function htmlHasText(html) {
  const source = String(html || '')
  if (/<img\b/i.test(source)) return true
  return source.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim().length > 0
}

function formatFileSize(size) {
  const n = Number(size)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n < 1024) return `${n.toLocaleString('ko-KR')} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${eventStatusBadgeClass(status)}`}>
      {eventStatusLabel(status)}
    </span>
  )
}

export default function EventsPage() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isOperator = useMemo(() => (user?.roles || []).includes(Roles.OPERATOR), [user?.roles])
  const isNew = location.pathname === '/events/new'
  const isEdit = Boolean(id) && location.pathname === `/events/${id}/edit`
  const detailId = id && !isNew && !isEdit ? id : null

  if (isNew) {
    return <EventCreateView isOperator={isOperator} navigate={navigate} />
  }
  if (isEdit) {
    return <EventCreateView isOperator={isOperator} navigate={navigate} editId={id} />
  }
  if (detailId) {
    return <EventDetailView id={detailId} />
  }
  return <EventListView isOperator={isOperator} />
}

function EventListView({ isOperator }) {
  const [items, setItems] = useState([])
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setErr('')
      try {
        const params = statusFilter === 'ALL' ? {} : { status: statusFilter }
        const res = await api.get('/events', { params })
        const payload = unwrapData(res)
        const list = Array.isArray(payload?.items) ? payload.items : []
        if (!ignore) setItems(list)
      } catch (e) {
        console.error(e)
        if (!ignore) setErr('이벤트 목록을 불러오지 못했습니다.')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [statusFilter])

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">이벤트</h1>
        </div>
        {isOperator && (
          <Link to="/events/new" className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
            이벤트 작성
          </Link>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b pb-3">
        {STATUS_OPTIONS.map((option) => {
          const active = statusFilter === option.id
          return (
            <button
              key={option.id}
              type="button"
              className={`rounded-full border px-3 py-1.5 text-sm font-medium ${active ? 'border-brand-navy bg-sky-50 text-brand-navy' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
              onClick={() => setStatusFilter(option.id)}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      <div className="rounded border">
        {loading ? (
          <div className="p-6 text-sm text-slate-600">불러오는 중…</div>
        ) : items.length > 0 ? (
          <div className="divide-y">
            {items.map((item) => (
              <Link key={item.id} to={`/events/${item.id}`} className="block px-4 py-4 hover:bg-slate-50">
                <div className="flex flex-wrap items-center gap-2">
                  {item.notice && (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                      공지
                    </span>
                  )}
                  <StatusBadge status={item.status} />
                  <div className="min-w-0 flex-1 truncate font-semibold text-slate-900">{item.title}</div>
                  <div className="text-xs text-slate-500">{formatMenuInstant(item, 'createdAt')}</div>
                </div>
                <div className="mt-2 line-clamp-2 text-sm text-slate-600">{item.contentPreview}</div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                  <span>{formatMenuInstant(item, 'eventStartAt')} ~ {formatMenuInstant(item, 'eventEndAt')}</span>
                  <span>댓글 {Number(item.commentCount || 0).toLocaleString('ko-KR')}</span>
                  {Number(item.attachmentCount || 0) > 0 && <span>첨부 {Number(item.attachmentCount || 0).toLocaleString('ko-KR')}</span>}
                  {item.createdByName && <span>작성자: {item.createdByName}</span>}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-slate-500">등록된 이벤트가 없습니다.</div>
        )}
      </div>
    </section>
  )
}

function EventCreateView({ isOperator, navigate, editId = null }) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [notice, setNotice] = useState(false)
  const [eventStartAt, setEventStartAt] = useState(defaultStartValue)
  const [eventEndAt, setEventEndAt] = useState(defaultEndValue)
  const [attachmentFiles, setAttachmentFiles] = useState([])
  const [existingAttachments, setExistingAttachments] = useState([])
  const [creating, setCreating] = useState(false)
  const [initialLoading, setInitialLoading] = useState(false)
  const [err, setErr] = useState('')
  const isEdit = Boolean(editId)

  useEffect(() => {
    if (!editId) return undefined
    let ignore = false
    async function loadForEdit() {
      setInitialLoading(true)
      setErr('')
      try {
        const res = await api.get(`/events/${editId}`)
        const payload = unwrapData(res)
        if (ignore) return
        setTitle(payload?.title || '')
        setContent(payload?.content || '')
        setNotice(Boolean(payload?.notice))
        setEventStartAt(toDatetimeLocalValue(payload?.eventStartAt || new Date()))
        setEventEndAt(toDatetimeLocalValue(payload?.eventEndAt || new Date()))
        setExistingAttachments(Array.isArray(payload?.attachments) ? payload.attachments : [])
      } catch (e) {
        console.error(e)
        if (!ignore) setErr('이벤트 내용을 불러오지 못했습니다.')
      } finally {
        if (!ignore) setInitialLoading(false)
      }
    }
    void loadForEdit()
    return () => { ignore = true }
  }, [editId])
  if (!isOperator) {
    return (
      <section className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">이벤트 작성</h1>
          <Link to="/events" className="rounded border px-3 py-2 text-sm hover:bg-slate-50">목록</Link>
        </div>
        <div className="rounded border p-6 text-sm text-slate-600">운영자만 이벤트를 작성할 수 있습니다.</div>
      </section>
    )
  }

  const onAttachmentChange = (event) => {
    const files = Array.from(event.target.files || [])
      .filter((file) => String(file?.type || '').startsWith('image/'))
      .slice(0, 10)
    setAttachmentFiles(files)
  }

  const removeAttachment = (index) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const removeExistingAttachment = (fileId) => {
    setExistingAttachments((prev) => prev.filter((file) => file.fileId !== fileId))
  }

  const uploadAttachments = async () => {
    const uploaded = existingAttachments.map((file) => file.fileId).filter(Boolean)
    for (const file of attachmentFiles) {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post('/files/upload/event-board-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const payload = unwrapData(res)
      if (payload?.fileId) uploaded.push(payload.fileId)
    }
    return uploaded
  }

  const onCreate = async (e) => {
    e.preventDefault()
    const cleanTitle = title.trim()
    if (!cleanTitle || !htmlHasText(content)) return
    const startIso = isoFromDatetimeLocal(eventStartAt)
    const endIso = isoFromDatetimeLocal(eventEndAt)
    if (!startIso || !endIso) {
      setErr('이벤트 일정을 입력해 주세요.')
      return
    }
    if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      setErr('종료일은 시작일 이후로 설정해 주세요.')
      return
    }
    try {
      setCreating(true)
      setErr('')
      const attachmentFileIds = await uploadAttachments()
      const payload = {
        title: cleanTitle,
        content,
        notice,
        eventStartAt: startIso,
        eventEndAt: endIso,
        attachmentFileIds,
      }
      const res = isEdit ? await api.patch(`/events/${editId}`, payload) : await api.post('/events', payload)
      const result = unwrapData(res)
      navigate(isEdit ? `/events/${editId}` : (result?.id ? `/events/${result.id}` : '/events'))
    } catch (e2) {
      console.error(e2)
      setErr(isEdit ? '이벤트 수정에 실패했습니다.' : '이벤트 등록에 실패했습니다.')
    } finally {
      setCreating(false)
    }
  }

  if (initialLoading) {
    return <div className="text-sm text-slate-600">불러오는 중...</div>
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">이벤트 작성</h1>
        <Link to="/events" className="rounded border px-3 py-2 text-sm hover:bg-slate-50">목록</Link>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      <form onSubmit={onCreate} className="rounded border p-4 space-y-4">
        <div className="space-y-1">
          <label className="text-sm text-slate-700" htmlFor="event-title">제목</label>
          <input
            id="event-title"
            className="w-full rounded border px-3 py-2"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={creating}
            required
          />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm text-slate-700" htmlFor="event-start-at">시작일</label>
            <input
              id="event-start-at"
              type="datetime-local"
              className="w-full rounded border px-3 py-2"
              value={eventStartAt}
              onChange={(e) => setEventStartAt(e.target.value)}
              disabled={creating}
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-700" htmlFor="event-end-at">종료일</label>
            <input
              id="event-end-at"
              type="datetime-local"
              className="w-full rounded border px-3 py-2"
              value={eventEndAt}
              onChange={(e) => setEventEndAt(e.target.value)}
              disabled={creating}
              required
            />
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300"
            checked={notice}
            onChange={(e) => setNotice(e.target.checked)}
            disabled={creating}
          />
          공지로 상단 고정
        </label>

        <div className="space-y-1">
          <label className="text-sm text-slate-700">내용</label>
          <SystemNoticeRichEditor
            value={content}
            onChange={setContent}
            uploadPath="/files/upload/event-board-image"
            placeholder="이벤트 내용을 입력하세요"
            testId="event-body-editor"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm text-slate-700" htmlFor="event-attachments">첨부 이미지</label>
          <input
            id="event-attachments"
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            multiple
            className="block w-full rounded border px-3 py-2 text-sm"
            onChange={onAttachmentChange}
            disabled={creating}
          />
          {existingAttachments.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {existingAttachments.map((file) => (
                <div key={file.fileId || file.id} className="flex items-center justify-between gap-2 rounded border bg-slate-50 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{file.name || file.fileId}</div>
                    <div className="text-xs text-slate-500">{formatFileSize(file.size) || file.contentType || '-'}</div>
                  </div>
                  <button
                    type="button"
                    className="rounded border bg-white px-2 py-1 text-xs hover:bg-slate-100"
                    onClick={() => removeExistingAttachment(file.fileId)}
                    disabled={creating}
                  >
                    제거
                  </button>
                </div>
              ))}
            </div>
          )}
          {attachmentFiles.length > 0 && (
            <div className="grid gap-2 sm:grid-cols-2">
              {attachmentFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className="flex items-center justify-between gap-2 rounded border bg-slate-50 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{file.name}</div>
                    <div className="text-xs text-slate-500">{formatFileSize(file.size)}</div>
                  </div>
                  <button
                    type="button"
                    className="rounded border bg-white px-2 py-1 text-xs hover:bg-slate-100"
                    onClick={() => removeAttachment(index)}
                    disabled={creating}
                  >
                    제거
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button type="submit" className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" disabled={creating}>
            {creating ? (isEdit ? '저장 중...' : '등록 중...') : (isEdit ? '이벤트 저장' : '이벤트 등록')}
          </button>
          <Link to={isEdit ? `/events/${editId}` : '/events'} className="rounded border px-4 py-2 text-sm hover:bg-slate-50">취소</Link>
        </div>
      </form>
    </section>
  )
}

function EventDetailView({ id }) {
  const navigate = useNavigate()
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [commentText, setCommentText] = useState('')
  const [anonymous, setAnonymous] = useState(true)
  const [commenting, setCommenting] = useState(false)
  const [deletingPost, setDeletingPost] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editingCommentText, setEditingCommentText] = useState('')
  const [editingCommentAnonymous, setEditingCommentAnonymous] = useState(true)
  const [savingCommentId, setSavingCommentId] = useState(null)
  const [deletingCommentId, setDeletingCommentId] = useState(null)

  async function loadDetail() {
    setLoading(true)
    setErr('')
    try {
      const res = await api.get(`/events/${id}`)
      setDetail(unwrapData(res))
    } catch (e) {
      console.error(e)
      setErr('이벤트 내용을 불러오지 못했습니다.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const onComment = async (e) => {
    e.preventDefault()
    const cleanContent = commentText.trim()
    if (!cleanContent) return
    try {
      setCommenting(true)
      await api.post(`/events/${id}/comments`, {
        content: cleanContent,
        anonymous,
      })
      setCommentText('')
      setAnonymous(true)
      await loadDetail()
    } catch (e2) {
      console.error(e2)
      setErr('댓글 등록에 실패했습니다.')
    } finally {
      setCommenting(false)
    }
  }

  const onDeletePost = async () => {
    if (!detail?.canDelete) return
    if (!window.confirm('이 이벤트를 삭제할까요?')) return
    try {
      setDeletingPost(true)
      await api.delete(`/events/${id}`)
      try { window?.toast?.show?.('이벤트가 삭제되었습니다.', 'success') } catch { }
      navigate('/events')
    } catch (e) {
      console.error(e)
      setErr('이벤트 삭제에 실패했습니다.')
    } finally {
      setDeletingPost(false)
    }
  }

  const startEditComment = (comment) => {
    if (!comment?.canEdit) return
    setEditingCommentId(comment.id)
    setEditingCommentText(comment.content || '')
    setEditingCommentAnonymous(Boolean(comment.anonymous))
    setErr('')
  }

  const cancelEditComment = () => {
    setEditingCommentId(null)
    setEditingCommentText('')
    setEditingCommentAnonymous(true)
  }

  const onSaveCommentEdit = async (commentId) => {
    if (!commentId) return
    const content = editingCommentText.trim()
    if (!content) return
    try {
      setSavingCommentId(commentId)
      await api.patch(`/events/${id}/comments/${commentId}`, {
        content,
        anonymous: editingCommentAnonymous,
      })
      await loadDetail()
      cancelEditComment()
      try { window?.toast?.show?.('댓글이 수정되었습니다.', 'success') } catch { }
    } catch (e) {
      console.error(e)
      setErr('댓글 수정에 실패했습니다.')
    } finally {
      setSavingCommentId(null)
    }
  }

  const onDeleteComment = async (comment) => {
    if (!comment?.id || !comment?.canDelete) return
    if (!window.confirm('이 댓글을 삭제할까요?')) return
    try {
      setDeletingCommentId(comment.id)
      await api.delete(`/events/${id}/comments/${comment.id}`)
      if (editingCommentId === comment.id) cancelEditComment()
      await loadDetail()
      try { window?.toast?.show?.('댓글이 삭제되었습니다.', 'success') } catch { }
    } catch (e) {
      console.error(e)
      setErr('댓글 삭제에 실패했습니다.')
    } finally {
      setDeletingCommentId(null)
    }
  }
  const comments = Array.isArray(detail?.comments) ? detail.comments : []
  const attachments = Array.isArray(detail?.attachments) ? detail.attachments : []

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">이벤트</h1>
        <div className="flex items-center gap-2">
          {detail?.canEdit && (
            <Link to={`/events/${id}/edit`} className="rounded border px-3 py-2 text-sm hover:bg-slate-50">수정</Link>
          )}
          {detail?.canDelete && (
            <button
              type="button"
              className="rounded border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
              disabled={deletingPost}
              onClick={onDeletePost}
            >
              {deletingPost ? '삭제 중...' : '삭제'}
            </button>
          )}
          <Link to="/events" className="rounded border px-3 py-2 text-sm hover:bg-slate-50">목록</Link>
        </div>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      {loading ? (
        <div className="text-sm text-slate-600">불러오는 중…</div>
      ) : detail ? (
        <div className="space-y-4">
          <article className="rounded border p-4 space-y-4">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                {detail.notice && (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                    공지
                  </span>
                )}
                <StatusBadge status={detail.status} />
                <h2 className="text-xl font-semibold text-slate-900">{detail.title}</h2>
              </div>
              <div className="text-xs text-slate-500">
                {formatMenuInstant(detail, 'eventStartAt')} ~ {formatMenuInstant(detail, 'eventEndAt')}
                {detail.createdByName ? ` · 작성자: ${detail.createdByName}` : ''}
                {detail.createdAt ? ` · 등록일: ${formatMenuInstant(detail, 'createdAt')}` : ''}
              </div>
            </div>

            <SanitizedRichText html={detail.content} className="system-notice-html" />

            {attachments.length > 0 && (
              <div className="border-t pt-4">
                <div className="mb-2 text-sm font-semibold">첨부 이미지</div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {attachments.map((file) => (
                    <a
                      key={file.id || file.fileId}
                      href={resolveApiAssetUrl(file.fileId)}
                      target="_blank"
                      rel="noreferrer"
                      className="overflow-hidden rounded border bg-white hover:border-slate-300"
                    >
                      <div className="aspect-video bg-slate-100">
                        <img src={resolveApiAssetUrl(file.fileId)} alt={file.name || '첨부 이미지'} className="h-full w-full object-cover" loading="lazy" />
                      </div>
                      <div className="px-3 py-2 text-xs text-slate-600">
                        <div className="truncate font-medium text-slate-800">{file.name || file.fileId}</div>
                        <div>{formatFileSize(file.size) || file.contentType || '-'}</div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </article>

          <div className="rounded border p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="font-semibold text-sm">댓글 참여</div>
              <div className="text-xs text-slate-500">{comments.length.toLocaleString('ko-KR')}건</div>
            </div>
            <form onSubmit={onComment} className="space-y-3">
              <textarea
                className="w-full rounded border px-3 py-2 min-h-[88px]"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                disabled={commenting}
                required
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300"
                    checked={anonymous}
                    onChange={(e) => setAnonymous(e.target.checked)}
                    disabled={commenting}
                  />
                  익명으로 참여
                </label>
                <button type="submit" className="rounded border bg-white px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60" disabled={commenting}>
                  {commenting ? '등록 중…' : '댓글 등록'}
                </button>
              </div>
            </form>

            <div className="mt-4 divide-y">
              {comments.map((comment) => (
                <div key={comment.id} className="py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                    <div className="font-medium text-slate-700">{comment.authorDisplayName || '익명'}</div>
                    <div className="flex items-center gap-2">
                      <span>{formatMenuInstant(comment, 'createdAt')}</span>
                      {editingCommentId !== comment.id && (comment.canEdit || comment.canDelete) && (
                        <span className="flex items-center gap-1">
                          {comment.canEdit && (
                            <button type="button" className="rounded border px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50" onClick={() => startEditComment(comment)}>
                              수정
                            </button>
                          )}
                          {comment.canDelete && (
                            <button type="button" className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60" disabled={deletingCommentId === comment.id} onClick={() => onDeleteComment(comment)}>
                              {deletingCommentId === comment.id ? '삭제 중...' : '삭제'}
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                  {editingCommentId === comment.id ? (
                    <div className="mt-2 space-y-2">
                      <textarea
                        className="min-h-[88px] w-full rounded border px-3 py-2 text-sm"
                        value={editingCommentText}
                        onChange={(event) => setEditingCommentText(event.target.value)}
                        disabled={savingCommentId === comment.id}
                      />
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300"
                            checked={editingCommentAnonymous}
                            onChange={(event) => setEditingCommentAnonymous(event.target.checked)}
                            disabled={savingCommentId === comment.id}
                          />
                          익명으로 표시
                        </label>
                        <div className="flex items-center gap-2">
                          <button type="button" className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60" disabled={savingCommentId === comment.id || !editingCommentText.trim()} onClick={() => onSaveCommentEdit(comment.id)}>
                            {savingCommentId === comment.id ? '저장 중...' : '저장'}
                          </button>
                          <button type="button" className="rounded border px-3 py-1.5 text-xs font-medium hover:bg-slate-50" disabled={savingCommentId === comment.id} onClick={cancelEditComment}>
                            취소
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{comment.content}</div>
                  )}
                </div>
              ))}
              {comments.length === 0 && (
                <div className="py-6 text-center text-sm text-slate-500">등록된 댓글이 없습니다.</div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded border p-8 text-center text-sm text-slate-500">이벤트를 찾을 수 없습니다.</div>
      )}
    </section>
  )
}